import PropTypes from "prop-types";
import * as React from "react";
import {
  IMAGE_QUALITY,
  outputSizeOf,
  qualityOf,
} from "../../../domain/productComposer/images";
import {
  Alert,
  Button,
  Dialog,
  SegmentedButton,
  Slider,
  Spinner,
  Typography,
} from "../../../themeV2";
import css from "./ImageEditor.module.css";
import readImage from "./readImage";

const Cropper = React.lazy(() => import("react-easy-crop"));

const WHITE = "#ffffff";
const BLACK = "#000000";

const backgroundFor = ({ choice, edgeColour }) =>
  choice === "white" ? WHITE : choice === "black" ? BLACK : edgeColour;

const qualityMessage = {
  [IMAGE_QUALITY.LOW]:
    "This image is below print resolution. It will look fine online, but consider a larger one.",
  [IMAGE_QUALITY.TOO_SMALL]:
    "This image is too small for this product type. Choose a larger image or zoom out.",
};

const ImageEditor = function ImageEditor({
  source,
  aspectRatio,
  requirements,
  onCancel,
  onSave,
}) {
  const [mode, setMode] = React.useState("crop");
  const [orientation, setOrientation] = React.useState("portrait");
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [cropPixels, setCropPixels] = React.useState(null);
  const [background, setBackground] = React.useState("auto");
  const [facts, setFacts] = React.useState(null);

  React.useEffect(() => {
    readImage(source).then(setFacts);
  }, [source]);

  const ratio =
    orientation === "landscape" && aspectRatio !== 1
      ? 1 / aspectRatio
      : aspectRatio;

  const output =
    facts && (mode === "fit" || cropPixels)
      ? outputSizeOf({
          mode,
          crop: cropPixels,
          natural: facts.natural,
          aspectRatio: ratio,
        })
      : null;
  const quality = output ? qualityOf({ requirements, ...output }) : null;
  const fill = facts
    ? backgroundFor({ choice: background, edgeColour: facts.edgeColour })
    : WHITE;

  const save = () =>
    onSave(
      mode === "fit"
        ? { mode, aspectRatio: ratio, background: fill, quality }
        : { mode, crop: cropPixels, quality },
    );

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onCancel()}>
      <Dialog.Popup size="lg" fullScreenOnCompact>
        <Dialog.Title>Edit image</Dialog.Title>

        <div className={css.controls}>
          <SegmentedButton.Group
            value={[mode]}
            onValueChange={([next]) => next && setMode(next)}
          >
            <SegmentedButton.Item value="crop">Crop</SegmentedButton.Item>
            <SegmentedButton.Item value="fit">Fit</SegmentedButton.Item>
          </SegmentedButton.Group>

          {aspectRatio !== 1 && (
            <SegmentedButton.Group
              value={[orientation]}
              onValueChange={([next]) => next && setOrientation(next)}
            >
              <SegmentedButton.Item value="portrait">
                Portrait
              </SegmentedButton.Item>
              <SegmentedButton.Item value="landscape">
                Landscape
              </SegmentedButton.Item>
            </SegmentedButton.Group>
          )}
        </div>

        <div className={css.stage} style={{ "--stage-ratio": ratio }}>
          {mode === "crop" ? (
            <React.Suspense fallback={<Spinner label="Loading editor" />}>
              <Cropper
                image={source}
                aspect={ratio}
                crop={crop}
                zoom={zoom}
                maxZoom={3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, pixels) => setCropPixels(pixels)}
              />
            </React.Suspense>
          ) : (
            <div className={css.fit} style={{ backgroundColor: fill }}>
              <img src={source} alt="Preview of the fitted image" />
            </div>
          )}
        </div>

        {mode === "crop" ? (
          <Slider
            label="Zoom"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onValueChange={setZoom}
          />
        ) : (
          <div className={css.backgrounds}>
            <Typography variant="label-lg" as="span">
              Background
            </Typography>
            <SegmentedButton.Group
              value={[background]}
              onValueChange={([next]) => next && setBackground(next)}
            >
              <SegmentedButton.Item value="auto">
                Match image
              </SegmentedButton.Item>
              <SegmentedButton.Item value="white">White</SegmentedButton.Item>
              <SegmentedButton.Item value="black">Black</SegmentedButton.Item>
            </SegmentedButton.Group>
          </div>
        )}

        {qualityMessage[quality] && (
          <Alert
            severity={quality === IMAGE_QUALITY.TOO_SMALL ? "error" : "warning"}
          >
            {qualityMessage[quality]}
          </Alert>
        )}

        <Dialog.Actions>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="filled"
            disabled={!quality || quality === IMAGE_QUALITY.TOO_SMALL}
            onClick={save}
          >
            Use image
          </Button>
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
};

ImageEditor.propTypes = {
  aspectRatio: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  requirements: PropTypes.shape({
    minImageDimension: PropTypes.number.isRequired,
    recommendedImageDimension: PropTypes.number.isRequired,
  }).isRequired,
  source: PropTypes.string.isRequired,
};

export default ImageEditor;
